import { ChangeDetectorRef, Component, ElementRef, forwardRef, Inject, NgZone, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import {
	DomSanitizer, SafeHtml
}                                                                                                             from '@angular/platform-browser';
import { ActivatedRoute, Router }                                                                             from '@angular/router';
import { WaitingForResponse }                                                                                 from '@cs/common';
import {
	CsToastManagerService
}                                                                                                             from '@cs/components/toast-manager';
import { CsHttpRequestOptions, LoggerUtil }                                                                   from '@cs/core';
import { LoginService }                                                                                       from '../state';
import {
	LoginConfigService
}                                                                                                             from '../login-config.service';

import { AuthenticationService } from '@cs/performance-manager/shared';
import { TranslateService }      from '@ngx-translate/core';
import { take, tap }             from 'rxjs/operators';


declare var grecaptcha: any;

@Component({
												standalone : false,
												selector   : 'pmc-otp',
												templateUrl: './otp.component.html'
											})
export class OtpComponent implements OnInit, OnDestroy {

	private get alreadyInjectedDependency() {
		return document.getElementById('googleCaptcha') != null;
	}

	/**
		* Flag for displaying the correct message
		*/
	userHasTOTP: boolean;

	/**
		* Binding to the username field
		*/
	otpValue     = '';
	/**
		* Binding to the amount of days authenticated
		*/
	auth2FADays  = false;
	/**
		* Binding to the captcha field
		*/
	captcha      = '';
	/**
		* The state of the form. Defaults to true because it's empty
		*/
	isErrorState = true;

	/**
		* HTML of the Captcha
		*/
	captchaHtml: SafeHtml;

	/**
		* Id of the rendered reCAPTCHA widget. Captured on the first render so a
		* later attempt (e.g. after a wrong OTP) can reset the existing widget by
		* id — clearing the consumed/expired token — instead of re-rendering the
		* same element, which throws "already rendered" and leaves the challenge in
		* a timed-out state (COH-3297). Null until rendered.
		*/
	private captchaWidgetId: number | null = null;

	/**
		* Indicator for the loader
		*/
	isLoadingCaptcha = true;

	//#region Loaders

	/**
		* Loader flag for the reset password button
		*/
	isWaitingForReset = false;

	/**
		* use google captcha
		*/
	useGoogleCaptcha = true;

	//#endregion


	constructor(private sanitizer: DomSanitizer,
													@Inject(forwardRef(() => LoginService)) private loginService: LoginService,
													@Inject(forwardRef(() => LoginConfigService)) public loginConfig: LoginConfigService,
													private l8n: TranslateService,
													private toastService: CsToastManagerService,
													private authenticationService: AuthenticationService,
													private renderer: Renderer2,
													private changeRef: ChangeDetectorRef,
													private route: ActivatedRoute,
													private ngzone: NgZone,
													private router: Router,
													private element: ElementRef) {
		window['onloadRecaptchaCallback'] = () => {
			this.refreshCaptcha();
		};

		// Receives the token once the (invisible) challenge completes. The form is
		// already validated in submitWithCaptcha() before the challenge runs.
		window['submitForm'] = (token) => this.ngzone.run(() => {
			this.captcha = token;
			this.verifyOtp();
		});

		this.l8n.get('LOGIN_WELCOME_MESSAGE')
						.subscribe(value => this.loginService.setTitleMessage(value));


	}

	ngOnInit() {
		this.loginConfig.initMultiFactor()
						.subscribe((result) => {
							this.isLoadingCaptcha     = false;
							const injectedRootUrlHtml = result.value.captchaHtml;
							this.userHasTOTP          = result.value.userHasTOTP;

							this.l8n.get(this.userHasTOTP
																				? 'APP_OTP_HEADER_MESSAGE'
																				: 'EMAIL_OTP_HEADER_MESSAGE')
											.subscribe(value => this.loginService.setUnderTitleMessage(value));
							this.auth2FADaysAmountOfDays = result.value.cookieLifetimeOptions[0];
							this.useGoogleCaptcha        = result.value.requiresGoogleCaptcha;
							this.captchaHtml             = this.sanitizer.bypassSecurityTrustHtml(injectedRootUrlHtml);
							// New container element — any previous widget id is stale.
							this.captchaWidgetId         = null;
							this.injectDependency();
							this.changeRef.detectChanges(); // TODO check if necessary
						});
	}

	ngOnDestroy(): void {
		this.captchaHtml = '';
		this.changeRef.detectChanges();
	}

	/**
		* Tear down the current reCAPTCHA widget and build a fresh one. Used after a
		* failed attempt instead of grecaptcha.reset(): resetting an invisible widget
		* rejects its internal promises and can leave the next execute() silently
		* dead (COH-3297). Recreating the container via the [innerHtml] binding
		* yields a virgin element, so refreshCaptcha() renders a brand-new widget.
		*/
	private rebuildCaptcha() {
		if (!this.useGoogleCaptcha) return;

		const captchaMarkup = this.captchaHtml;
		this.captchaHtml    = '';
		this.changeRef.detectChanges();
		document.querySelector('.grecaptcha-badge')?.remove();
		this.captchaWidgetId = null;
		this.captchaHtml     = captchaMarkup;
		this.changeRef.detectChanges();
		this.refreshCaptcha();
	}

	refreshCaptcha() {
		setTimeout(() => {
			const container = this.element.nativeElement.querySelector('.reset-password .g-recaptcha');
			if (container) {
				try {
					// Rendered-ness is stamped on the container ELEMENT, not inferred from
					// child count — the invisible widget's badge may be re-parented, which
					// would make the container look "empty" and cause a second render on
					// the same element, corrupting grecaptcha's internals (COH-3297).
					if (container.getAttribute('data-cs-rendered') !== 'true') {
						// Fresh (or recreated) container: render our own widget. The script
						// loads with render=explicit, so this is the only render. A badge
						// left behind by a previous widget instance is orphaned — remove it.
						if (this.captchaWidgetId !== null) {
							document.querySelector('.grecaptcha-badge')?.remove();
						}
						// data-bind is stripped so grecaptcha does NOT also bind the submit
						// button — a bound widget goes dead after grecaptcha.reset();
						// submitWithCaptcha() drives execute() explicitly instead.
						container.removeAttribute('data-bind');
						this.captchaWidgetId = grecaptcha.render(container, {
							'sitekey': container.getAttribute('data-sitekey'),
							// The server div declares no data-size; explicit render then
							// defaults to the visible checkbox, so pin the invisible mode
							// this flow is designed for (execute() from submitWithCaptcha).
							'size':    container.getAttribute('data-size') ?? 'invisible',
							'callback': (output) => {
								window['submitForm'](output);
							}
						});
						container.setAttribute('data-cs-rendered', 'true');
					} else if (this.captchaWidgetId !== null) {
						// Live widget (e.g. after a failed OTP verification): clear the
						// consumed/expired token so the next attempt runs a fresh challenge.
						grecaptcha.reset(this.captchaWidgetId);
					}
				} catch (e) {
					LoggerUtil.log(`Failed to render/reset the captcha: ${e?.message ?? e}`);
				}
			}
			this.changeRef.detectChanges();
		}, 0);
	}

	/**
		* Submit entry point for the captcha flow (button click): validate the form
		* first, then programmatically run the invisible challenge. The token arrives
		* in window.submitForm, which performs the actual OTP verification.
		*/
	submitWithCaptcha() {
		const form = document.getElementById('form') as HTMLFormElement;
		if (form && !form.checkValidity()) {
			form.reportValidity();
			return;
		}

		if (this.captchaWidgetId !== null) {
			try {
				grecaptcha.execute(this.captchaWidgetId);
			} catch (e) {
				LoggerUtil.log(`Failed to execute the captcha: ${e?.message ?? e}`);
			}
		} else {
			// Widget never rendered (script/network hiccup) — try to recover.
			this.refreshCaptcha();
		}
	}

	/**
		* Request a new email with OTP
		*/
	requestNewEmail() {
		const options: CsHttpRequestOptions = {
			errorResponseHandler: (error): boolean => {
				let message = error.statusText;
				try {
					// we might received more useful messages in the body
					const result = JSON.parse(error.message);
					if (result.hasOwnProperty('messages')) {
						message = (<string[]>result.messages).join(' ');
					}
				} catch (e) {
					if (error.error && error.error.hasOwnProperty('messages'))
						message = error.error.messages.join(' ');
					else if (error.error)
						message = error.error;
					else if (error.status === 500) {
						return false;
					}

				}
				this.toastService.show({
																												title  : error.status === 500
																																					? this.l8n.instant('ERROR_SOMETHING_WRONG')
																																					: this.l8n.instant('INFO'),
																												content: message,
																												type   : error.status === 500
																																					? 'error'
																																					: 'info'
																											});

				// Rebuild — not grecaptcha.reset(): resetting an invisible widget can
				// leave the next execute() dead (COH-3297).
				this.rebuildCaptcha();
				return true;

			},
			headers             : null
		};
		this.loginConfig.requestNewOtp(options)
						.subscribe(() => {
							this.toastService.show({
																															content: this.l8n.instant('NEW_OTP_EMAIL_REQUESTED'),
																															type   : 'info'
																														});
						});
	}

	/**
		* Send the otp to the server, it contains the captcha
		*/
	verifyOtp() {

		const options: CsHttpRequestOptions = {
			errorResponseHandler: (error): boolean => {

				let message = error.statusText;
				try {
					// we might received more useful messages in the body
					const result = JSON.parse(error.message);
					if (result.hasOwnProperty('messages')) {
						message = (<string[]>result.messages).join(' ');
					}
				} catch (e) {
					if (error.error && error.error.hasOwnProperty('messages'))
						message = error.error.messages.join(' ');
					else if (error.error)
						message = error.error;
					else if (error.status === 500) {
						return false;
					}

				}
				this.toastService.show({
																												title  : error.status === 500
																																					? this.l8n.instant('ERROR_SOMETHING_WRONG')
																																					: this.l8n.instant('INFO'),
																												content: message,
																												type   : error.status === 500
																																					? 'error'
																																					: 'info'
																											});

				// Rebuild — not grecaptcha.reset(): resetting an invisible widget can
				// leave the next execute() dead (COH-3297).
				this.rebuildCaptcha();
				return true;


			},
			headers             : null
		};

		this.loginConfig.verifyOtp(this.otpValue, this.captcha, this.auth2FADays
																																																										? this.auth2FADaysAmountOfDays
																																																										: '0', options)
						.pipe(tap(WaitingForResponse.new(isLoading => this.isWaitingForReset = isLoading)))
						.subscribe((result) => {
							this.router.navigate(['/'], {relativeTo: this.route.root});
						});
	}

	getErrorMessage() {
		if (this.isLoadingCaptcha) {
			return this.l8n.instant('CAPTCHA_STILL_LOADING');
		} else if (this.otpValue.length === 0) {
			return this.l8n.instant('NO_CODE_PROVIDED_MAIL');
		}
	}

	checkErrorState() {
		this.isErrorState = (this.useGoogleCaptcha && this.captcha.length === 0) || this.otpValue.length === 0 || this.isLoadingCaptcha;
	}


	goBackOfLogout() {
		this.authenticationService.logOut();
	}

	private auth2FADaysAmountOfDays: string;

	private injectDependency(): void {
		if (this.alreadyInjectedDependency) {
			this.refreshCaptcha();
			return;
		}

		const script   = document.createElement('script');
		script.id      = 'googleCaptcha';
		// render=explicit: WE render the widget (refreshCaptcha) — auto-render would
		// bind the button via data-bind, and a bound widget breaks after a reset().
		script.src     = 'https://www.google.com/recaptcha/api.js?onload=onloadRecaptchaCallback&render=explicit';
		script.type    = 'text/javascript';
		script.async   = true;
		script.defer   = true;
		script.charset = 'utf-8';
		this.renderer.appendChild(document.head, script);
	}
}
